import {
  Button,
  ButtonsWrapper,
  Form,
  Icon,
  ICONS,
  Modal,
  PLATFORM_THEME_COLORS,
  SelectInput,
  TextAreaInput,
  Typography,
  UploadInput,
} from '@thinknet-gmbh/platform-common-ui';
import { useTranslation } from 'react-i18next';

import { APPLICANT_FILE_MAX_SIZE } from 'src/components/applicant-request-page/components/general-form-items/general-form-items.constants';
import { useGetCurrentCompanyId } from 'src/hooks/companies/get-current-company-id.hook';
import { useGetDocumentLabelsByCompany } from 'src/hooks/documents-labels/get-documents-labels.hook';
import { useCreateDocumentTemplate } from 'src/hooks/documents-templates/create-document-template.hook';
import { DraggerContent } from 'src/pages/settings/components/company-settings-pages/documents/components/upload-template-modal/upload-template-modal.styles';

const UploadDocumentModal = ({
  isOpened,
  onClose,
  employeeId,
}: {
  isOpened: boolean;
  onClose: () => void;
  employeeId: string;
}) => {
  const { t } = useTranslation();
  const currentCompanyId = useGetCurrentCompanyId();
  const { documentsLabels } = useGetDocumentLabelsByCompany(currentCompanyId);
  const { createDocumentTemplate, loading } = useCreateDocumentTemplate({ onSuccess: onClose });

  const onFinish = (values: any) => {
    const { template, description, documentLabelId } = values;

    createDocumentTemplate({
      variables: {
        payload: {
          companyId: currentCompanyId,
          description,
          documentLabelId,
          file: [template.file],
          memberId: employeeId,
          title: template.file.name,
        },
      },
    });
  };

  return (
    <Modal
      isOpened={isOpened}
      title={t('settings:documents.labels.uploadNewDocument')}
      onClose={onClose}
    >
      <Form layout="vertical" onFinish={onFinish}>
        <Form.Item
          name="documentLabelId"
          label={t('common:labels.label')}
          rules={[{ required: true, message: t('common:validation.error.labelRequired') }]}
        >
          <SelectInput placeholder={t('settings:documents.labels.selectLabel')}>
            {documentsLabels.map((label: any) => (
              <SelectInput.Option key={label._id} value={label._id}>
                {label.name}
              </SelectInput.Option>
            ))}
          </SelectInput>
        </Form.Item>
        <Form.Item name="description" label={t('common:labels.description')}>
          <TextAreaInput
            showCount
            maxLength={70}
            placeholder={t('settings:supportTab.someDetails')}
          />
        </Form.Item>

        <Form.Item
          name="template"
          label={t('settings:documents.buttons.uploadFile')}
          valuePropName="file"
          rules={[
            { required: true, message: t('settings:documents.errors.templateRequired') },
            () => ({
              validator(_, { file }) {
                if (file.size > APPLICANT_FILE_MAX_SIZE) {
                  return Promise.reject(
                    new Error(t('settings:documents.errors.wrongFileSize', { detail: 15 }))
                  );
                }

                return Promise.resolve();
              },
            }),
          ]}
        >
          <UploadInput.Dragger
            accept=".doc, .docx, .xls, .xlsx, .pdf"
            maxCount={1}
            beforeUpload={() => false}
          >
            <DraggerContent>
              <Icon
                icon={ICONS.inbox}
                color={PLATFORM_THEME_COLORS.primary.blue}
                size={26}
                rightMargin={false}
              />
              <Typography>{t('settings:documents.labels.dragBoxText')}</Typography>
            </DraggerContent>
          </UploadInput.Dragger>
        </Form.Item>

        <ButtonsWrapper>
          <Button type="primary" htmlType="submit" loading={loading}>
            {t('common:buttons.save')}
          </Button>
          <Button onClick={onClose}>{t('common:buttons.cancel')}</Button>
        </ButtonsWrapper>
      </Form>
    </Modal>
  );
};

export default UploadDocumentModal;
